import { getLocaleCookieName } from '../i18n/cookie';

const MAX_PREFERRED_LOCALES = 20;
const MAX_LOCALE_LENGTH = 100;

const localeTag = (value: string | undefined) =>
  value &&
  value.length <= MAX_LOCALE_LENGTH &&
  /^[a-z]{1,8}(?:[-_][a-z0-9]{1,8})*$/i.test(value)
    ? value.replaceAll('_', '-')
    : undefined;

// These are browser preferences, independent of the app's chosen translation.
// Missing or restricted browser APIs must not interrupt analytics delivery.
export const browserVisitContext = () => {
  let timeZone: string | undefined;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    /* unavailable */
  }

  let languages: readonly string[] = [];
  try {
    if (typeof navigator !== 'undefined') {
      languages = navigator.languages?.length
        ? navigator.languages
        : [navigator.language];
    }
  } catch {
    /* unavailable */
  }
  const preferredLocales = languages
    .slice(0, MAX_PREFERRED_LOCALES)
    .flatMap((language) => localeTag(language) ?? []);

  return {
    time_zone: timeZone,
    preferred_locales: preferredLocales.length ? preferredLocales : undefined
  };
};

// Use the document and Rhino's optional preference cookie so analytics does
// not import a translation runtime or load any language catalogs.
export const browserLocaleContext = () => {
  if (typeof document === 'undefined') return {};
  const appLocale = localeTag(document.documentElement.lang);
  if (!appLocale) return {};

  let source: 'automatic' | 'explicit' | undefined;
  try {
    const prefix = `${getLocaleCookieName()}=`;
    const saved = document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith(prefix))
      ?.slice(prefix.length);
    const preference = saved ? localeTag(decodeURIComponent(saved)) : undefined;
    if (!saved) source = 'automatic';
    else if (preference)
      source =
        preference.toLowerCase() === appLocale.toLowerCase()
          ? 'explicit'
          : 'automatic';
  } catch {
    // A blocked or malformed cookie does not establish the selection source.
  }

  return { app_locale: appLocale, locale_source: source };
};
